import { inject, injectable } from 'inversify';
import type {
  IBondScreenerConfig,
  IBondScreenerViewsResponse,
  ICreateBondScreenerViewPayload,
  ICreateBondScreenerViewResponse,
  IUpdateBondScreenerViewPayload,
  IRenameBondScreenerViewPayload,
  IDeleteBondScreenerViewPayload,
  IActivateBondScreenerViewPayload,
  IUseDefaultBondScreenerViewPayload,
  TBondScreenerType,
  TBondScreenerFiltersShape,
} from '@/creditflowresearch/domain/vo/Bondscreener';
import { withServerCache } from '@/creditflowresearch/infrastructure/cache/withServerCache';
import Request from '@framework/api/Rpc/Request';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import type { IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';

@injectable()
class BondScreenerViewsRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
  ) {}

  @withServerCache({ scope: 'BondscreenerViews' })
  async getConfig<TFilters extends TBondScreenerFiltersShape>(
    type: TBondScreenerType,
  ): Promise<IBondScreenerConfig<TFilters>> {
    const request = new Request('cfr.bondscreener.view.get_config', { type });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async getAllViews<TFilters extends TBondScreenerFiltersShape>(
    type: TBondScreenerType,
  ): Promise<IBondScreenerViewsResponse<TFilters>> {
    const request = new Request('cfr.bondscreener.view.get_all', { type });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async createView<TFilters extends TBondScreenerFiltersShape>(
    payload: ICreateBondScreenerViewPayload<TFilters>,
  ): Promise<ICreateBondScreenerViewResponse> {
    const request = new Request('cfr.bondscreener.view.create', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  async updateView<TFilters extends TBondScreenerFiltersShape>(
    payload: IUpdateBondScreenerViewPayload<TFilters>,
  ): Promise<void> {
    const request = new Request('cfr.bondscreener.view.update', payload);
    await this.rpc.call<RpcSuccess>(request);
  }

  async renameView(payload: IRenameBondScreenerViewPayload): Promise<void> {
    const request = new Request('cfr.bondscreener.view.rename', payload);
    await this.rpc.call<RpcSuccess>(request);
  }

  async deleteView(payload: IDeleteBondScreenerViewPayload): Promise<void> {
    const request = new Request('cfr.bondscreener.view.delete', payload);
    await this.rpc.call<RpcSuccess>(request);
  }

  async activateView(payload: IActivateBondScreenerViewPayload): Promise<void> {
    const request = new Request('cfr.bondscreener.view.activate', payload);
    await this.rpc.call<RpcSuccess>(request);
  }

  async useDefaultView(payload: IUseDefaultBondScreenerViewPayload): Promise<void> {
    const request = new Request('cfr.bondscreener.view.use_default', payload);
    await this.rpc.call<RpcSuccess>(request);
  }
}

export default BondScreenerViewsRepository;
